import React, { useState } from "react";
import { useMediaQuery, Box, Button, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const OutreachMessageGenerator = ({ showSnackbar, handleCopy }) => {
    const isMobile = useMediaQuery("(max-width: 1024px)");
    const [outreachMessage, setOutreachMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [cooldown, setCooldown] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const handleGenerateMessage = async () => {
        if (cooldown) return;

        setIsLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/generate-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    prompt: "Generate a unique and engaging welcome message for a new follower on social media.",
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to generate message");
            }

            const data = await response.json();
            const generatedMessage = data.message;

            setOutreachMessage(generatedMessage);
            handleCopy(generatedMessage);
            showSnackbar("Outreach message generated and copied to clipboard!");
        } catch (error) {
            console.error("Error generating message:", error);
            showSnackbar("Failed to generate outreach message. Please try again.");
        } finally {
            setIsLoading(false);
            setCooldown(true);
            setCountdown(9);
            const intervalId = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(intervalId);
                        setCooldown(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
    };

    const handleManualCopy = () => {
        if (!outreachMessage) return;

        navigator.clipboard.writeText(outreachMessage).then(() => {
            showSnackbar("Outreach message copied to clipboard!");
        }).catch(() => {
            showSnackbar("Failed to copy message. Please copy manually.");
        });
    };

    const buttonText = isLoading
        ? "Generating..."
        : cooldown
            ? `${countdown}...`
            : "Generate Message";

    return (
        <Box
            sx={{
                padding: "8px 8px",
                backgroundColor: "#1c1c1c",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
            }}
        >
            <Button
                variant="contained"
                onClick={handleGenerateMessage}
                disabled={isLoading || cooldown}
                sx={{
                    width: isMobile ? "100%" : "230px",
                    height: "40px",
                    border: "1px solid #333",
                    backgroundColor: "#232323",
                    color: "#ddd",
                    textShadow: "0px 0px 4px #777",
                    "&:hover": {
                        color: "#98FF98",
                        textShadow: "0px 0px 4px #98FF98",
                        backgroundColor: "#2a2a2a"
                    },
                }}
            >
                <AutoAwesomeIcon sx={{ mr: 1 }} /> {buttonText}
            </Button>
            {isMobile ? (
                <Typography
                    variant="body2"
                    onClick={handleManualCopy}
                    sx={{
                        border: "2px dotted #555",
                        borderRadius: "4px",
                        py: "8px",
                        px: "8px",
                        width: "100%",
                        color: "#ccc",
                        mt: 0.75,
                        textAlign: "center",
                        textShadow: "0px 0px 4px #777",
                        boxShadow: "1px 1px 8px #151515",
                        cursor: "pointer",
                        "&:hover": { color: "#98FF98", textShadow: "0px 0px 6px #98FF98" },
                    }}
                >
                    {outreachMessage || '"Generate Message" button AI generates outreach message that automatically copies to your clipboard to paste.'}
                </Typography>
            ) : (
                <Tooltip title={outreachMessage || ""} arrow disableHoverListener={!outreachMessage}>
                    <Typography
                        variant="body2"
                        onClick={handleManualCopy}
                        sx={{
                            border: "2px dotted #333",
                            borderRadius: "4px",
                            py: "8px",
                            px: "8px",
                            width: "82%",
                            color: "#ccc",
                            mt: 0,
                            ml: 1,
                            textAlign: "center",
                            textShadow: "0px 0px 4px #777",
                            boxShadow: "1px 1px 8px #151515",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            "&:hover": { color: "#98FF98", textShadow: "0px 0px 6px #98FF98" },
                        }}
                    >
                        {outreachMessage || '"Generate Message" button AI generates outreach message that automatically copies to your clipboard to paste.'}
                    </Typography>
                </Tooltip>
            )}
        </Box>
    );
};

export default OutreachMessageGenerator;
